import { useState, useEffect } from "react";
import supabase from "./supabase";
import { T, withAlpha } from "./theme";

const FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
    title: "AI Coach that remembers you",
    desc: "Not generic tips. Real insights based on your actual habits, mood, finances, and health, all connected.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    ),
    title: "Habits · Finance · Health in one place",
    desc: "Stop juggling five apps. Track money, health, habits and wellbeing side by side, each scored on its own terms.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C6.5 2 2 6 2 11c0 2.4 1 4.6 2.6 6.2L4 22l4.8-2.4C10 20.2 11 20.4 12 20.4c5.5 0 10-4 10-9.4C22 5.9 17.5 2 12 2z"/>
      </svg>
    ),
    title: "Personalized daily briefing",
    desc: "Every morning: your top 3 priorities, streak alerts, upcoming bills, and one cross-domain pattern only your coach can see.",
  },
];

export default function WaitlistPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [count, setCount] = useState(null);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    supabase.from("waitlist").select("id", { count: "exact", head: true })
      .then(({ count: c }) => { if (typeof c === "number") setCount(c); });
  }, []);

  const spotsLeft = count !== null ? Math.max(0, 25 - count) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    const res = await fetch("/api/waitlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: trimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (data.error === "already_exists") {
      setStatus("success"); // already on list — treat as success
    } else if (!res.ok || data.error) {
      setStatus("error");
      setErrorMsg("Something went wrong. Try again in a moment.");
    } else {
      setStatus("success");
      if (count !== null) setCount(c => c + 1);
    }
  };

  return (
    // All colors from T (src/theme.js) — this public page must match the app.
    // It shipped hardcoded dark/amber while the app rendered cream/crimson.
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'DM Sans', sans-serif", overflowX: "hidden" }}>
      {/* Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          50%  { transform: scale(1.18); opacity: 0; }
          100% { transform: scale(1);   opacity: 0; }
        }
        .wl-input::placeholder { color: ${T.textDim}; }
        .wl-input:focus { outline: none; border-color: ${T.accent} !important; box-shadow: 0 0 0 3px ${withAlpha(T.accent, 0.18)}; }
        .wl-btn:hover:not(:disabled) { background: ${T.accentPressed} !important; transform: translateY(-1px); box-shadow: 0 8px 28px ${withAlpha(T.accent, 0.45)} !important; }
        .wl-btn:active:not(:disabled) { transform: translateY(0); }
        .wl-btn { transition: all 0.18s ease; }
      `}</style>

      {/* Radial glow backdrop */}
      <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 0, background: `radial-gradient(ellipse 70% 50% at 50% 0%, ${withAlpha(T.accent, 0.09)} 0%, transparent 70%)` }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 620, margin: "0 auto", padding: isMobile ? "32px 20px calc(60px + env(safe-area-inset-bottom, 0px))" : "72px 32px 80px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>

        {/* Wordmark */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "0ms", marginBottom: isMobile ? 48 : 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
            <div style={{ position: "relative", width: 32, height: 32, flexShrink: 0 }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: T.accent, opacity: 0.25, animation: "pulse-ring 2.5s ease-out infinite" }} />
              <div style={{ position: "relative", width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.accent}, ${T.accentPressed})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              </div>
            </div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: T.text, letterSpacing: "-0.01em" }}>Scorify</span>
          </div>
        </div>

        {/* Founding member badge */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "80ms", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: withAlpha(T.accent, 0.12), border: `1px solid ${withAlpha(T.accent, 0.35)}`, borderRadius: 99, padding: "7px 16px" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.accent, letterSpacing: "0.04em" }}>
              FOUNDING MEMBER
            </span>
            <span style={{ width: 1, height: 12, background: withAlpha(T.accent, 0.3) }} />
            <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>
              {spotsLeft !== null ? `${spotsLeft} of 25 spots left` : "Only 25 spots"}. Free for life
            </span>
          </div>
        </div>

        {/* Hero headline */}
        <h1 style={{ animation: "fadeUp 0.5s ease both", animationDelay: "140ms", margin: "0 0 18px", fontFamily: "'DM Serif Display', serif", fontSize: isMobile ? 36 : 56, fontWeight: 400, color: T.text, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
          Your life,{" "}
          <span style={{ background: `linear-gradient(90deg, ${T.accent}, ${T.accentPressed}, ${T.accent})`, backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "shimmer 3s linear infinite" }}>
            finally in sync.
          </span>
        </h1>

        {/* Subheadline */}
        <p style={{ animation: "fadeUp 0.5s ease both", animationDelay: "200ms", margin: "0 0 40px", fontSize: isMobile ? 16 : 18, color: T.textMuted, lineHeight: 1.65, maxWidth: 460, fontWeight: 400 }}>
          AI-powered habits, finance, health and wellness, with a coach that actually knows you.
        </p>

        {/* Email form */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "260ms", width: "100%", maxWidth: 480 }}>
          {status === "success" ? (
            <div style={{ background: withAlpha(T.success, 0.12), border: `1px solid ${withAlpha(T.success, 0.4)}`, borderRadius: 14, padding: "22px 28px", textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", margin: "0 auto" }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 6 }}>You're in!</div>
              <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>We'll be in touch soon. Welcome to the founding member list.</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10 }}>
              <input
                className="wl-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrorMsg(""); }}
                disabled={status === "loading"}
                style={{ flex: 1, background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "13px 18px", fontSize: 16, color: T.text, fontFamily: "inherit", transition: "border-color 0.15s, box-shadow 0.15s" }}
              />
              <button
                className="wl-btn"
                type="submit"
                disabled={status === "loading"}
                style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 10, padding: "13px 26px", fontSize: 15, fontWeight: 700, cursor: status === "loading" ? "not-allowed" : "pointer", whiteSpace: "nowrap", fontFamily: "inherit", opacity: status === "loading" ? 0.7 : 1, boxShadow: `0 4px 20px ${withAlpha(T.accent, 0.35)}`, letterSpacing: "0.01em", width: isMobile ? "100%" : "auto", minHeight: 52 }}>
                {status === "loading" ? "Joining…" : "Join the Waitlist"}
              </button>
            </form>
          )}
          {errorMsg && (
            <div style={{ marginTop: 10, fontSize: 13, color: T.danger, textAlign: "left" }}>{errorMsg}</div>
          )}
          {status !== "success" && (
            <p style={{ marginTop: 12, fontSize: 12, color: T.textDim, letterSpacing: "0.01em" }}>No spam. No credit card. Ever.</p>
          )}
        </div>

        {/* Divider */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "320ms", width: "100%", maxWidth: 480, margin: "56px 0 0", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <span style={{ fontSize: 11, color: T.textDim, letterSpacing: "0.08em", textTransform: "uppercase" }}>What you're getting</span>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {/* Feature cards */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "380ms", width: "100%", display: "flex", flexDirection: "column", gap: 12, marginTop: 28 }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 18, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: isMobile ? "16px 16px" : "18px 22px", textAlign: "left", transition: "border-color 0.15s", cursor: "default" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = withAlpha(T.accent, 0.3)}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: withAlpha(T.accent, 0.12), display: "flex", alignItems: "center", justifyContent: "center", color: T.accent, flexShrink: 0 }}>
                {f.icon}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4, letterSpacing: "-0.01em" }}>{f.title}</div>
                <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Social proof / teaser */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "440ms", marginTop: 52, padding: "22px 28px", background: withAlpha(T.accent, 0.06), border: `1px solid ${withAlpha(T.accent, 0.15)}`, borderRadius: 14, width: "100%", maxWidth: 480 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: isMobile ? 18 : 20, color: T.text, lineHeight: 1.4, marginBottom: 14 }}>
            "Most apps track one thing. Scorify sees the connections you can't."
          </div>
          <div style={{ fontSize: 12, color: T.textDim, letterSpacing: "0.04em", textTransform: "uppercase" }}>Founding member preview</div>
        </div>

        {/* Footer */}
        <div style={{ animation: "fadeUp 0.5s ease both", animationDelay: "500ms", marginTop: 64, fontSize: 13, color: T.textDim, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <span>Made by Rocco</span>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.borderStrong, flexShrink: 0 }} />
          <a href="https://www.scorify.to" style={{ color: T.textDim, textDecoration: "none" }} onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textDim}>scorify.to</a>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.borderStrong, flexShrink: 0 }} />
          <a href="/login" style={{ color: T.textDim, textDecoration: "none" }} onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textDim}>Sign in →</a>
        </div>
      </div>
    </div>
  );
}
